import React from 'react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import '../styles/InstallAppModal.css';

/**
 * InstallAppModal Component
 *
 * Prompts users to install the Vibary PWA on their device.
 * Shows different content for iOS (manual instructions) vs Android/Desktop (native prompt).
 *
 * Props:
 * - isOpen: boolean - Whether the modal is visible
 * - onClose: function - Handler to close the modal (temporary dismiss)
 * - onDismiss: function - Handler for "Don't show again"
 * - onInstall: function - Handler to trigger native install prompt
 * - isIOSSafari: boolean - Whether user is on iOS Safari
 */
function InstallAppModal({ isOpen, onClose, onDismiss, onInstall, isIOSSafari }) {
  useEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  const handleInstall = async () => {
    if (onInstall) {
      const accepted = await onInstall();
      if (accepted) {
        onClose();
      }
    }
  };

  return (
    <div className="install-modal-overlay" onClick={onClose}>
      <div className="install-modal" onClick={(e) => e.stopPropagation()}>
        <button className="install-modal-close" onClick={onClose} aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="install-modal-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 2L12 15M12 15L8 11M12 15L16 11"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4 17V19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 20.1046 20 19V17"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h2 className="install-modal-title">Install Vibary</h2>
        <p className="install-modal-description">
          Add Vibary to your home screen for quick access and a better experience.
        </p>

        {isIOSSafari ? (
          <div className="install-modal-ios-steps">
            <div className="ios-step">
              <div className="ios-step-number">1</div>
              <div className="ios-step-content">
                <span>Tap the</span>
                <svg className="ios-share-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M12 2L12 15M12 2L8 6M12 2L16 6" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M4 12V19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 20.1046 20 19V12" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Share button</span>
              </div>
            </div>
            <div className="ios-step">
              <div className="ios-step-number">2</div>
              <div className="ios-step-content">
                <span>Scroll down and tap</span>
                <strong>"Add to Home Screen"</strong>
              </div>
            </div>
            <div className="ios-step">
              <div className="ios-step-number">3</div>
              <div className="ios-step-content">
                <span>Tap</span>
                <strong>"Add"</strong>
                <span>in the top right</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="install-modal-benefits">
            <div className="install-benefit">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>Quick access from home screen</span>
            </div>
            <div className="install-benefit">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>Full screen experience</span>
            </div>
            <div className="install-benefit">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>Works offline</span>
            </div>
          </div>
        )}

        <div className="install-modal-actions">
          {isIOSSafari ? (
            <button className="install-modal-primary" onClick={onClose}>
              Got it
            </button>
          ) : (
            <button className="install-modal-primary" onClick={handleInstall}>
              Install App
            </button>
          )}
          <button className="install-modal-dismiss" onClick={onDismiss}>
            Don't show again
          </button>
        </div>
      </div>
    </div>
  );
}

export default InstallAppModal;
